import { Component, OnInit, OnDestroy, ChangeDetectorRef, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { NgbPaginationModule, NgbTooltipModule, NgbAlertModule, NgbDropdownModule, NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { AuthService } from '../../services/auth.service';
import { AuthUser } from '../../interfaces/auth.interface';
import { StaffHeaderComponent } from '../staff-header/staff-header.component';
import { StaffNavComponent } from '../staff-nav/staff-nav.component';
import { csvFileDateSuffix, downloadCsv } from '../../utils/csv.util';

// Role interfaces
export interface RoleDetails {
  id: string;
  roleName: string;
  roleCode: string;
  description: string;
  permissions: string[];
  isActive: boolean;
  userCount: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface RoleFilters {
  searchTerm: string;
  roleCode: string;
  status: 'all' | 'active' | 'inactive';
  hasUsers: 'all' | 'with-users' | 'without-users';
}

@Component({
  selector: 'app-role-management',
  standalone: true,
  imports: [
    CommonModule, 
    FormsModule, 
    NgbPaginationModule, 
    NgbTooltipModule, 
    NgbAlertModule,
    NgbDropdownModule,
    StaffHeaderComponent, 
    StaffNavComponent
  ],
  templateUrl: './role-management.component.html',
  styleUrl: './role-management.component.scss'
})
export class RoleManagementComponent implements OnInit, OnDestroy {
  currentUser: AuthUser | null = null;
  isMobileMenuOpen = false;
  private userSubscription: Subscription | undefined;
  
  roles: RoleDetails[] = [];
  filteredRoles: RoleDetails[] = [];
  isLoading = false;
  isExporting = false;
  error: string | null = null;
  showMockDataWarning = false;

  // Filter properties
  filters: RoleFilters = {
    searchTerm: '',
    roleCode: '',
    status: 'all',
    hasUsers: 'all'
  };

  // Pagination
  page = 1;
  pageSize = 10;
  readonly pageSizeOptions = [10, 20, 50, 100];
  collectionSize = 0;
  
  // Sorting
  sortField = '';
  sortDirection: 'asc' | 'desc' = 'asc';

  private subscription = new Subscription();

  constructor(
    private authService: AuthService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private modalService: NgbModal
  ) {}

  ngOnInit(): void {
    console.log('🔄 RoleManagementComponent ngOnInit เริ่มต้น');
    
    // Subscribe to auth user first
    this.userSubscription = this.authService.currentUser$.subscribe(user => {
      console.log('👤 Current user updated:', user);
      this.currentUser = user;
      this.cdr.detectChanges();
    });

    // Use NgZone to ensure operations run inside Angular zone
    this.ngZone.run(() => {
      setTimeout(() => {
        console.log('⏰ เริ่มโหลดข้อมูลหลังจาก timeout (inside NgZone)');
        this.loadRoles();
      }, 100);
    });
  }

  ngOnDestroy(): void {
    console.log('🗑️ RoleManagementComponent ngOnDestroy');
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
    this.subscription.unsubscribe();
  }

  onLogout(): void {
    this.authService.logout();
  }

  toggleMobileMenu(): void {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
  }

  onNavigate(route: string): void {
    this.closeMobileMenu();
    
    switch (route) {
      case 'users':
        this.router.navigate(['/staff/user-management']);
        break;
      case 'roles':
        // Already on role management page
        break;
      case 'courses':
        this.router.navigate(['/staff/dashboard']);
        break;
      case 'reports':
        this.router.navigate(['/staff/dashboard']);
        break;
      case 'settings':
        this.router.navigate(['/staff/dashboard']);
        break;
      default:
        this.router.navigate(['/staff/dashboard']);
        break;
    }
  }

  /**
   * Load all roles from the server
   */
  loadRoles(): void {
    console.log('🚀 เริ่มโหลดข้อมูล roles...');
    
    this.isLoading = true;
    this.error = null;
    this.showMockDataWarning = false;
    
    this.cdr.detectChanges();

    // Mock data for demonstration
    setTimeout(() => {
      this.ngZone.run(() => {
        const mockRoles: RoleDetails[] = [
          {
            id: 'role-1',
            roleName: 'ผู้ดูแลระบบ',
            roleCode: 'ADMIN',
            description: 'มีสิทธิ์เข้าถึงและจัดการระบบทั้งหมด',
            permissions: ['user.create', 'user.read', 'user.update', 'user.delete', 'role.create', 'role.read', 'role.update', 'role.delete', 'system.manage'],
            isActive: true,
            userCount: 3,
            createdAt: '2024-01-15T10:00:00Z',
            updatedAt: '2024-01-20T14:30:00Z',
            createdBy: 'system'
          },
          {
            id: 'role-2',
            roleName: 'เจ้าหน้าที่ VAT',
            roleCode: 'VAT_OFFICER',
            description: 'จัดการข้อมูลสมาชิก VAT และอนุมัติใบรับรอง',
            permissions: ['user.read', 'user.update', 'certificate.create', 'certificate.approve', 'report.view'],
            isActive: true,
            userCount: 12,
            createdAt: '2024-01-15T10:00:00Z',
            updatedAt: '2024-02-01T09:15:00Z',
            createdBy: 'admin'
          },
          {
            id: 'role-3',
            roleName: 'เจ้าหน้าที่หลักสูตร',
            roleCode: 'COURSE_MANAGER',
            description: 'จัดการหลักสูตรและการอบรม CPD',
            permissions: ['course.create', 'course.read', 'course.update', 'course.delete', 'cpd.manage', 'report.view'],
            isActive: true,
            userCount: 8,
            createdAt: '2024-01-15T10:00:00Z',
            updatedAt: '2024-01-25T16:45:00Z',
            createdBy: 'admin'
          },
          {
            id: 'role-4',
            roleName: 'ผู้ตรวจสอบ',
            roleCode: 'AUDITOR',
            description: 'ตรวจสอบและรายงานข้อมูลในระบบ',
            permissions: ['user.read', 'report.view', 'audit.read'],
            isActive: true,
            userCount: 5,
            createdAt: '2024-01-15T10:00:00Z',
            updatedAt: '2024-01-18T11:20:00Z',
            createdBy: 'admin'
          },
          {
            id: 'role-5',
            roleName: 'ผู้ช่วยเจ้าหน้าที่',
            roleCode: 'ASSISTANT',
            description: 'ช่วยงานด้านข้อมูลและการประสานงาน',
            permissions: ['user.read', 'report.view'],
            isActive: false,
            userCount: 0,
            createdAt: '2024-01-15T10:00:00Z',
            updatedAt: '2024-02-10T13:00:00Z',
            createdBy: 'admin'
          }
        ];

        console.log('✅ โหลดข้อมูล roles สำเร็จ:', mockRoles.length, 'รายการ');
        
        this.roles = [...mockRoles];
        this.showMockDataWarning = true;
        this.applyFilters();
        
        this.isLoading = false;
        this.cdr.detectChanges();
      });
    }, 1000);
  }

  /**
   * Apply filters to role list
   */
  applyFilters(): void {
    console.log('🔍 เริ่ม applyFilters - roles:', this.roles.length, 'filters:', this.filters);
    
    if (!this.roles || this.roles.length === 0) {
      console.log('⚠️ ไม่มีข้อมูล roles สำหรับการกรอง');
      this.filteredRoles = [];
      this.collectionSize = 0;
      this.page = 1;
      this.cdr.detectChanges();
      return;
    }

    try {
      this.filteredRoles = this.roles.filter(role => {
        // Search term filter
        if (this.filters.searchTerm) {
          const searchTerm = this.filters.searchTerm.toLowerCase();
          const matchesSearch = 
            role.roleName.toLowerCase().includes(searchTerm) ||
            role.roleCode.toLowerCase().includes(searchTerm) ||
            role.description.toLowerCase().includes(searchTerm);
          if (!matchesSearch) return false;
        }

        // Role code filter
        if (this.filters.roleCode && !role.roleCode.toLowerCase().includes(this.filters.roleCode.toLowerCase())) {
          return false;
        }

        // Status filter
        if (this.filters.status !== 'all') {
          if (this.filters.status === 'active' && !role.isActive) return false;
          if (this.filters.status === 'inactive' && role.isActive) return false;
        }

        // Has users filter
        if (this.filters.hasUsers !== 'all') {
          if (this.filters.hasUsers === 'with-users' && role.userCount === 0) return false;
          if (this.filters.hasUsers === 'without-users' && role.userCount > 0) return false;
        }

        return true;
      });

      this.collectionSize = this.filteredRoles.length;
      this.page = 1;
      
      console.log('✅ applyFilters สำเร็จ - filteredRoles:', this.filteredRoles.length, 'collectionSize:', this.collectionSize);
      this.cdr.detectChanges();
      
    } catch (error) {
      console.error('❌ เกิดข้อผิดพลาดใน applyFilters:', error);
      this.filteredRoles = [...this.roles];
      this.collectionSize = this.roles.length;
      this.page = 1;
      this.cdr.detectChanges();
    }
  }

  /**
   * Get current page roles
   */
  getCurrentPageRoles(): RoleDetails[] {
    console.log('📖 getCurrentPageRoles called - page:', this.page, 'pageSize:', this.pageSize, 'filteredRoles:', this.filteredRoles.length);
    
    if (!this.filteredRoles || this.filteredRoles.length === 0) {
      console.log('⚠️ No filtered roles available');
      return [];
    }
    
    const startIndex = (this.page - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    const currentPageRoles = this.filteredRoles.slice(startIndex, endIndex);
    
    console.log('📄 Page calculation - startIndex:', startIndex, 'endIndex:', endIndex, 'result:', currentPageRoles.length, 'roles');
    
    return currentPageRoles;
  }

  /**
   * Clear all filters
   */
  clearFilters(): void {
    this.filters = {
      searchTerm: '',
      roleCode: '',
      status: 'all',
      hasUsers: 'all'
    };
    this.applyFilters();
  }

  /**
   * Sort roles by field
   */
  sortBy(field: string): void {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDirection = 'asc';
    }

    this.filteredRoles.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (field) {
        case 'roleName':
          aValue = a.roleName || '';
          bValue = b.roleName || '';
          break;
        case 'roleCode':
          aValue = a.roleCode || '';
          bValue = b.roleCode || '';
          break;
        case 'userCount':
          aValue = a.userCount || 0;
          bValue = b.userCount || 0;
          break;
        case 'createdAt':
          aValue = new Date(a.createdAt || '');
          bValue = new Date(b.createdAt || '');
          break;
        case 'updatedAt':
          aValue = new Date(a.updatedAt || '');
          bValue = new Date(b.updatedAt || '');
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return this.sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return this.sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }

  /**
   * Get sort icon for column
   */
  getSortIcon(field: string): string {
    if (this.sortField !== field) return '';
    return this.sortDirection === 'asc' ? '▲' : '▼';
  }

  /**
   * Change page size
   */
  changePageSize(size: number): void {
    this.pageSize = size;
    this.page = 1;
    this.collectionSize = this.filteredRoles.length;
  }

  /**
   * Toggle role status
   */
  toggleRoleStatus(role: RoleDetails): void {
    const newStatus = !role.isActive;
    const confirmation = confirm(
      `คุณต้องการ${newStatus ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}บทบาท "${role.roleName}" หรือไม่?`
    );

    if (confirmation) {
      // TODO: Implement API call
      role.isActive = newStatus;
      role.updatedAt = new Date().toISOString();
      
      // Show success message
      alert(`✅ ${newStatus ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}บทบาท "${role.roleName}" เรียบร้อยแล้ว`);
    }
  }

  /**
   * Create new role
   */
  createNewRole(): void {
    // TODO: Implement role creation modal
    alert('ฟีเจอร์สร้างบทบาทใหม่กำลังพัฒนา');
  }

  /**
   * Edit role
   */
  editRole(role: RoleDetails): void {
    console.log('🔧 Edit role:', role);
    
    // TODO: Implement role editing functionality
    alert(`แก้ไขบทบาท: ${role.roleName}\n\nฟีเจอร์นี้กำลังพัฒนา`);
  }

  /**
   * Delete role with confirmation
   */
  deleteRole(role: RoleDetails): void {
    console.log('🗑️ Delete role:', role);
    
    if (role.userCount > 0) {
      alert(`❌ ไม่สามารถลบบทบาท "${role.roleName}" ได้\n\nเนื่องจากมีผู้ใช้งาน ${role.userCount} คนที่ใช้บทบาทนี้อยู่`);
      return;
    }
    
    const confirmation = confirm(
      `⚠️ คุณต้องการลบบทบาท "${role.roleName}" หรือไม่?\n\n` +
      `การดำเนินการนี้ไม่สามารถยกเลิกได้!`
    );

    if (!confirmation) {
      return;
    }

    // Double confirmation for safety
    const doubleConfirmation = confirm(
      `❗ ยืนยันการลบบทบาทอีกครั้ง?\n\n` +
      `บทบาท: ${role.roleName}\n` +
      `รหัส: ${role.roleCode}\n\n` +
      `พิมพ์ "DELETE" เพื่อยืนยัน`
    );

    if (doubleConfirmation) {
      // TODO: Implement actual delete functionality with API call
      const index = this.roles.findIndex(r => r.id === role.id);
      if (index > -1) {
        this.roles.splice(index, 1);
        this.applyFilters();
        alert(`✅ ลบบทบาท "${role.roleName}" เรียบร้อยแล้ว`);
      }
    }
  }

  /**
   * View role permissions
   */
  viewRolePermissions(role: RoleDetails): void {
    const permissionsList = role.permissions.join('\n• ');
    alert(`สิทธิ์การใช้งานของบทบาท "${role.roleName}":\n\n• ${permissionsList}`);
  }

  /**
   * Manage role users
   */
  manageRoleUsers(role: RoleDetails): void {
    // TODO: Implement user assignment to roles
    alert(`จัดการผู้ใช้งานในบทบาท "${role.roleName}"\n\nฟีเจอร์นี้กำลังพัฒนา`);
  }

  /**
   * Export roles to CSV
   */
  exportToCSV(): void {
    if (this.isExporting) {
      return;
    }

    console.log('📊 Exporting to CSV...');

    // หน้านี้กรอง/แบ่งหน้าฝั่ง client จึง Export จากรายการที่ผ่านตัวกรองได้ทันที
    const rows = this.filteredRoles || [];

    if (rows.length === 0) {
      alert('ไม่พบข้อมูลที่ตรงกับเงื่อนไขการกรอง');
      return;
    }

    this.isExporting = true;
    this.cdr.detectChanges();

    const headers = [
      'ชื่อบทบาท',
      'รหัสบทบาท',
      'คำอธิบาย',
      'จำนวนสิทธิ์',
      'สิทธิ์การใช้งาน',
      'จำนวนผู้ใช้งาน',
      'สร้างโดย',
      'วันที่สร้าง',
      'แก้ไขล่าสุด',
      'สถานะ'
    ];

    const csvRows = rows.map(role => [
      role.roleName,
      role.roleCode,
      role.description || '-',
      role.permissions?.length || 0,
      (role.permissions || []).join(', ') || '-',
      role.userCount ?? 0,
      role.createdBy || '-',
      this.formatDate(role.createdAt),
      this.formatDate(role.updatedAt),
      role.isActive ? 'ใช้งาน' : 'ปิดใช้งาน'
    ]);

    downloadCsv(`roles_${csvFileDateSuffix()}`, headers, csvRows);
    console.log(`✅ Exported ${csvRows.length} rows to CSV`);

    this.isExporting = false;
    this.cdr.detectChanges();
  }

  /**
   * Refresh data
   */
  refreshData(): void {
    console.log('🔄 รีเฟรชข้อมูลทั้งหมด...');
    this.loadRoles();
  }

  /**
   * Get status class for styling
   */
  getStatusClass(role: RoleDetails): string {
    return role.isActive ? 'status-active' : 'status-inactive';
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string | undefined): string {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('th-TH');
  }

  /**
   * Track by function for ngFor optimization
   */
  trackByRoleId(index: number, role: RoleDetails): string {
    return role.id;
  }

  /**
   * Math helper for template
   */
  min(a: number, b: number): number {
    return Math.min(a, b);
  }

  /**
   * Handle dropdown open/close state for proper z-index management
   */
  onDropdownOpenChange(isOpen: boolean, roleId: string): void {
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        const dropdownButton = document.getElementById(`dropdownMenuButton-${roleId}`);
        const tableRow = dropdownButton?.closest('tr');
        const tableContainer = dropdownButton?.closest('.table-responsive');
        
        if (tableRow) {
          if (isOpen) {
            tableRow.classList.add('dropdown-active');
            tableRow.style.position = 'relative';
            tableRow.style.zIndex = '1050';
            
            if (tableContainer) {
              (tableContainer as HTMLElement).style.overflow = 'visible';
            }
            
            const rowRect = tableRow.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const dropdownMenu = tableRow.querySelector('.dropdown-menu') as HTMLElement;
            
            if (dropdownMenu && rowRect.bottom > viewportHeight - 200) {
              dropdownMenu.style.top = 'auto';
              dropdownMenu.style.bottom = '100%';
              dropdownMenu.style.marginBottom = '2px';
              dropdownMenu.style.marginTop = '0';
            }
          } else {
            tableRow.classList.remove('dropdown-active');
            tableRow.style.position = '';
            tableRow.style.zIndex = '';
            
            if (tableContainer && window.innerWidth >= 992) {
              (tableContainer as HTMLElement).style.overflow = 'visible';
            } else if (tableContainer) {
              (tableContainer as HTMLElement).style.overflow = '';
            }
            
            const dropdownMenu = tableRow.querySelector('.dropdown-menu') as HTMLElement;
            if (dropdownMenu) {
              dropdownMenu.style.top = '';
              dropdownMenu.style.bottom = '';
              dropdownMenu.style.marginBottom = '';
              dropdownMenu.style.marginTop = '';
            }
          }
        }
      }, 0);
    });
  }
}
